import { useCallback, useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { FpShell } from "@/pages/panchatanthra/fp/FpShell";
import { fpBase, inr, useFpContext } from "@/lib/fp";
import { toast } from "sonner";

const AREAS = ["", "revenue", "other_income", "pvc", "dvc", "fixed_cost", "depreciation",
  "interest", "tax", "working_capital", "cash", "fixed_assets", "debt", "equity", "wastage",
  "excluded"];

const tone = (c) => (c >= 0.85 ? "bg-success/18 text-success"
  : c >= 0.6 ? "bg-warning/25 text-[hsl(30_88%_30%)]" : "bg-destructive/15 text-destructive");
const label = (c) => (c >= 0.85 ? "Confident" : c >= 0.6 ? "Check" : "Needs you");

/** A free-text tag that also suggests the engagement's master list. */
const Tag = ({ value, onChange, placeholder, options, testid, listId }) => (
  <>
    <input value={value} placeholder={placeholder} list={listId} data-testid={testid}
           onChange={(e) => onChange(e.target.value)}
           className="w-32 rounded-lg border border-border bg-card px-2 py-1.5 text-[11px] text-navy outline-none focus:border-terracotta" />
    <datalist id={listId}>
      {(options || []).map((o) => <option key={o} value={o} />)}
    </datalist>
  </>
);

export default function FpMappingReview() {
  const { tenantId, period } = useFpContext();
  const [data, setData] = useState(null);
  const [masters, setMasters] = useState({});
  const [edits, setEdits] = useState({});
  const [picked, setPicked] = useState({});
  const [view, setView] = useState("suggested");

  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      const [out, ms] = await Promise.all([
        api.get(`${fpBase(tenantId)}/mappings/review`, { params: { status: view } }),
        api.get(`${fpBase(tenantId)}/masters`),
      ]);
      setData(out.data);
      setMasters(ms.data);
      setEdits({});
      setPicked({});
    } catch (e) { setData({ error: errMsg(e) }); }
  }, [tenantId, view]);

  useEffect(() => { load(); }, [load]);

  const patch = (id, field, value) =>
    setEdits((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));

  const overrides = () => Object.entries(edits).map(([id, fields]) => ({ id, ...fields }));

  const approve = async (all) => {
    const ids = Object.keys(picked).filter((k) => picked[k]);
    try {
      const { data: out } = await api.post(`${fpBase(tenantId)}/mappings/approve`,
        { approve_all: all, ids, overrides: overrides() });
      toast.success(`${out.approved} classification(s) approved — they will be reused every month`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const resuggest = async () => {
    try {
      const { data: out } = await api.post(`${fpBase(tenantId)}/mappings/suggest`, null,
                                           { params: { period } });
      const added = Object.values(out.sources || {})
        .reduce((s, v) => s + (v.new_suggestions || 0), 0);
      toast.success(added ? `${added} new line(s) to review` : "No new lines — all already classified");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const rows = data?.rows || [];
  return (
    <FpShell showPeriod={false}
      title="Classification review"
      description="The engine reads each line of the uploaded statements and proposes where it belongs. Nothing is counted until you approve it — and once approved, the same classification is reused for every future month automatically. Override anything before approving."
      actions={
        <>
          <button onClick={resuggest} data-testid="fp-review-resuggest"
                  className="rounded-full border border-border px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] text-navy hover:bg-muted">
            Re-read statements
          </button>
          <button onClick={() => approve(false)} data-testid="fp-review-approve-selected"
                  className="rounded-full bg-navy px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-navy/90">
            Approve selected
          </button>
          <button onClick={() => approve(true)} data-testid="fp-review-approve-all"
                  className="rounded-full bg-terracotta px-4 py-2.5 text-[10px] uppercase tracking-[0.14em] text-white hover:bg-terracotta/90">
            Approve all
          </button>
        </>
      }
    >
      <div className="space-y-5" data-testid="fp-mapping-review">
        <div className="panel flex flex-wrap items-center justify-between gap-4 p-5">
          <div className="flex flex-wrap gap-5">
            {Object.entries(data?.counts || {}).map(([k, v]) => (
              <div key={k} data-testid={`fp-review-count-${k}`}>
                <p className="label-caps">{k}</p>
                <p className="font-mono text-2xl tabular-nums text-navy">{v}</p>
              </div>
            ))}
          </div>
          <div className="flex overflow-hidden rounded-full border border-border">
            {[["suggested", "Awaiting approval"], ["approved", "Approved"], ["all", "Everything"]]
              .map(([k, l]) => (
                <button key={k} onClick={() => setView(k)} data-testid={`fp-review-view-${k}`}
                        className={`px-4 py-2 text-[10px] font-bold uppercase tracking-[0.12em] ${
                          view === k ? "bg-navy text-white" : "bg-card text-navy hover:bg-muted"}`}>
                  {l}
                </button>
              ))}
          </div>
        </div>

        {data?.error ? (
          <p className="panel p-6 text-sm text-destructive">{data.error}</p>
        ) : !rows.length ? (
          <p className="panel p-8 text-sm text-muted-foreground" data-testid="fp-review-empty">
            Nothing here. Upload a Profit &amp; Loss, Balance Sheet, budget or stock statement under
            Statements and the engine will propose a classification for every line.
          </p>
        ) : (
          <div className="panel overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-muted/50 text-[10px] font-bold uppercase tracking-[0.1em] text-muted-foreground">
                  <tr>
                    <th className="px-3 py-3"></th>
                    <th className="px-3 py-3 text-left">Line in the statement</th>
                    <th className="px-3 py-3 text-right">Amount</th>
                    <th className="px-3 py-3 text-left">Engine's judgment</th>
                    <th className="px-3 py-3 text-left">Category tag</th>
                    <th className="px-3 py-3 text-left">Cost component</th>
                    <th className="px-3 py-3 text-left">Business function</th>
                    <th className="px-3 py-3 text-left">Stream / channel</th>
                    <th className="px-3 py-3 text-left">Why</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map((r) => {
                    const e = edits[r.id] || {};
                    const area = e.destination_area ?? r.destination_area ?? "";
                    return (
                      <tr key={r.id} data-testid={`fp-review-row-${r.id}`}
                          className={picked[r.id] ? "bg-terracotta/5" : ""}>
                        <td className="px-3 py-2.5">
                          <input type="checkbox" checked={!!picked[r.id]}
                                 data-testid={`fp-review-pick-${r.id}`}
                                 onChange={(ev) => setPicked({ ...picked, [r.id]: ev.target.checked })} />
                        </td>
                        <td className="px-3 py-2.5">
                          <p className="font-medium text-navy">{r.source_value}</p>
                          <p className="text-[10px] text-muted-foreground">
                            {r.source_code} · {r.source_field}
                          </p>
                        </td>
                        <td className="px-3 py-2.5 text-right font-mono tabular-nums text-navy">
                          {inr(r.amount_seen)}
                        </td>
                        <td className="px-3 py-2.5">
                          <select value={area} data-testid={`fp-review-area-${r.id}`}
                                  onChange={(ev) => patch(r.id, "destination_area", ev.target.value)}
                                  className="w-40 rounded-lg border border-border bg-card px-2 py-1.5 text-[11px] text-navy outline-none focus:border-terracotta">
                            {AREAS.map((a) => (
                              <option key={a} value={a}>{a ? a.replace(/_/g, " ") : "— rolls up to its group —"}</option>
                            ))}
                          </select>
                          <input value={e.control_item ?? r.control_item ?? ""}
                                 placeholder="control item"
                                 data-testid={`fp-review-control-${r.id}`}
                                 onChange={(ev) => patch(r.id, "control_item", ev.target.value)}
                                 className="mt-1 w-40 rounded-lg border border-border bg-card px-2 py-1.5 text-[11px] text-navy outline-none focus:border-terracotta" />
                        </td>
                        <td className="px-3 py-2.5">
                          <Tag value={e.destination_value ?? r.destination_value ?? ""}
                               placeholder="none" testid={`fp-review-category-${r.id}`}
                               listId={`cats-${r.id}`}
                               options={masters?.product_categories?.items}
                               onChange={(v) => patch(r.id, "destination_value", v)} />
                        </td>
                        <td className="px-3 py-2.5">
                          <Tag value={e.cost_component ?? r.cost_component ?? ""}
                               placeholder="none" testid={`fp-review-component-${r.id}`}
                               listId={`comp-${r.id}`}
                               options={[...(masters?.pvc_components?.items || []),
                                         ...(masters?.dvc_components?.items || []),
                                         ...(masters?.fixed_cost_lines?.items || [])]}
                               onChange={(v) => patch(r.id, "cost_component", v)} />
                          <Tag value={e.cost_sub_component ?? r.cost_sub_component ?? ""}
                               placeholder="RM / FG" testid={`fp-review-subcomponent-${r.id}`}
                               listId={`sub-${r.id}`} options={["RM", "FG"]}
                               onChange={(v) => patch(r.id, "cost_sub_component", v)} />
                        </td>
                        <td className="px-3 py-2.5">
                          <Tag value={e.business_function ?? r.business_function ?? ""}
                               placeholder="none" testid={`fp-review-function-${r.id}`}
                               listId={`fn-${r.id}`} options={masters?.business_functions?.items}
                               onChange={(v) => patch(r.id, "business_function", v)} />
                        </td>
                        <td className="px-3 py-2.5">
                          <Tag value={e.revenue_stream ?? r.revenue_stream ?? ""}
                               placeholder="stream" testid={`fp-review-stream-${r.id}`}
                               listId={`rs-${r.id}`} options={masters?.revenue_streams?.items}
                               onChange={(v) => patch(r.id, "revenue_stream", v)} />
                          <Tag value={e.channel ?? r.channel ?? ""}
                               placeholder="channel" testid={`fp-review-channel-${r.id}`}
                               listId={`dc-${r.id}`} options={masters?.distribution_channels?.items}
                               onChange={(v) => patch(r.id, "channel", v)} />
                        </td>
                        <td className="max-w-xs px-3 py-2.5">
                          <span className={`rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.1em] ${tone(r.confidence || 0)}`}>
                            {label(r.confidence || 0)}
                          </span>
                          <p className="mt-1 text-[10px] leading-snug text-muted-foreground">
                            {r.suggested_reason}
                          </p>
                          {r.approved_by_email && (
                            <p className="text-[9px] text-success">Approved by {r.approved_by_email}</p>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </FpShell>
  );
}
